// Confirm delivery (P4.1; 01-SPEC §3 step 7, D57). Pure rules: which lines are delivered, what is
// still to come, and whether a confirmation is allowed. Over-receipt is refused; only items in
// good condition count as received, so damaged or wrong items stay outstanding until replaced.

import { z } from 'zod';
import type { DeliveryState } from '../rules/statuses';

export const DN_NO = /^[A-Z]{3}-DN-\d{4,}-\d{4}$/;

export const CONDITIONS = ['good', 'damaged', 'wrong'] as const;
export type Condition = (typeof CONDITIONS)[number];

/** Monthly (recurring service) lines have no delivery note (01-SPEC §3 step 7). */
export const deliverable = (lineType: string): boolean => lineType !== 'monthly';

const text = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .regex(/^[^<>]*$/, 'Remove < and >');

const qty = z
  .number()
  .nonnegative('Cannot be negative')
  .max(1_000_000)
  .refine((v) => Math.abs(v * 1000 - Math.round(v * 1000)) < 1e-6, 'Up to 3 decimals');

export const deliveryInputSchema = z.strictObject({
  /** ETag of the PO row the page showed; a delivery confirmed meanwhile makes this one fail. */
  etag: z.string().min(1).max(200),
  lines: z
    .array(
      z.strictObject({
        lineNo: z.number().int().min(1).max(500),
        qty,
        condition: z.enum(CONDITIONS).default('good'),
      }),
    )
    .min(1)
    .max(50),
  rating: z.number().int().min(1, 'Rate the supplier').max(5),
  supplierDnRef: text(60).default(''),
  notes: text(1000).default(''),
});
export type DeliveryInput = z.infer<typeof deliveryInputSchema>;

export const problemSchema = z.strictObject({
  notes: text(1000).min(1, 'Say what went wrong'),
});

export interface PoLineFacts {
  lineNo: number;
  qty: number;
  lineType: string;
  receivedToDate: number;
}

/** Quantities are kept to 3 decimals; this avoids 0.1 + 0.2 style drift. */
const round3 = (v: number) => Math.round(v * 1000) / 1000;

export const outstanding = (l: PoLineFacts): number =>
  deliverable(l.lineType) ? Math.max(0, round3(l.qty - l.receivedToDate)) : 0;

export function deliveryState(lines: readonly PoLineFacts[]): DeliveryState {
  const items = lines.filter((l) => deliverable(l.lineType));
  if (items.length === 0) return 'notRequired';
  if (items.every((l) => outstanding(l) === 0)) return 'full';
  return items.some((l) => l.receivedToDate > 0) ? 'partial' : 'none';
}

export interface ReceiptCheck {
  /** Field errors keyed `line.<lineNo>` or `lines`; empty when the receipt is valid. */
  errors: Record<string, string>;
  /** Received-to-date per line number after this delivery (good items only). */
  received: Map<number, number>;
  state: DeliveryState;
  /** Line numbers still waiting for items after this delivery. */
  stillToCome: number[];
  /** Some items arrived damaged or wrong. */
  problem: boolean;
}

/**
 * Checks one delivery against the PO lines: each line at most once, deliverable, and no more
 * arrived than is still outstanding (whatever its condition). At least one item must arrive.
 */
export function checkReceipt(
  lines: readonly PoLineFacts[],
  input: Pick<DeliveryInput, 'lines'>,
): ReceiptCheck {
  const errors: Record<string, string> = {};
  const byNo = new Map(lines.map((l) => [l.lineNo, l]));
  const received = new Map(lines.map((l) => [l.lineNo, l.receivedToDate]));
  const seen = new Set<number>();
  let any = false;
  let problem = false;
  for (const r of input.lines) {
    const key = `line.${r.lineNo}`;
    const line = byNo.get(r.lineNo);
    if (!line || seen.has(r.lineNo) || !deliverable(line.lineType)) {
      errors[key] = 'Not a line to receive on this PO';
      continue;
    }
    seen.add(r.lineNo);
    if (r.qty === 0) continue;
    const open = outstanding(line);
    if (r.qty > open) {
      errors[key] = open === 0 ? 'Already received in full' : `Can't exceed ${open} outstanding`;
      continue;
    }
    any = true;
    if (r.condition === 'good') received.set(r.lineNo, round3(line.receivedToDate + r.qty));
    else problem = true;
  }
  if (!any && Object.keys(errors).length === 0) errors.lines = 'Enter what arrived';
  const after = lines.map((l) => ({ ...l, receivedToDate: received.get(l.lineNo) ?? 0 }));
  return {
    errors,
    received,
    state: deliveryState(after),
    stillToCome: after.filter((l) => outstanding(l) > 0).map((l) => l.lineNo),
    problem,
  };
}

export interface DeliveryLineView {
  lineNo: number;
  description: string;
  unit: string;
  ordered: number;
  receivedBefore: number;
  outstanding: number;
}

export interface DeliveryNoteView {
  dnNo: string;
  /** 1 for the first delivery on the PO, 2 for the next… */
  seq: number;
  receivedAt: string;
  receivedBy: string;
  onBehalf: boolean;
  partial: boolean;
  rating: number;
  supplierDnRef: string;
}

export interface DeliveryPageBody {
  prNo: string;
  poNo: string;
  title: string;
  supplierName: string;
  requesterName: string;
  /** PO row ETag; the confirmation sends it back. */
  etag: string;
  state: DeliveryState;
  lines: DeliveryLineView[];
  deliveries: DeliveryNoteView[];
  /** The viewer may confirm now (requester, or Procurement on their behalf). */
  canConfirm: boolean;
  /** The viewer confirms on the requester's behalf (Procurement): logged and flagged. */
  onBehalf: boolean;
}

export interface DeliveryConfirmedBody {
  dnNo: string;
  seq: number;
  state: DeliveryState;
  status: string;
}

/**
 * What has been received of a PO so far, incl. VAT, in minor units (P5.1, D66): each line's value
 * (a monthly line counts all its months) times the share received. For the dashboard's
 * "delivered" figure on partly delivered POs.
 */
export function receivedValue(
  lines: readonly {
    qty: number;
    unitPrice: number;
    lineType: string;
    months: number;
    received: number;
  }[],
  vatRatePct: number,
): number {
  let net = 0;
  for (const l of lines) {
    if (l.qty <= 0) continue;
    const value = l.qty * l.unitPrice * (l.lineType === 'monthly' ? Math.max(1, l.months) : 1);
    net += value * Math.min(1, Math.max(0, l.received / l.qty));
  }
  return Math.round(net * (1 + vatRatePct / 100));
}
